import { useCallback, useEffect, useState } from 'react';
import { resolveInstallOffer } from '../utils/installPrompt';
import { readInstallEnvironment, safely } from '../utils/installEnvironment';

/**
 * "Add to Home Screen", made obvious.
 *
 * ⭐⭐ THE APP IS ALREADY AN INSTALLABLE PWA - standalone manifest, a service worker, an
 * apple-touch-icon - and nobody knew, because nothing on the screen ever said so. Chrome hides
 * its own offer behind a menu item and Safari hides it inside the Share sheet. So the install
 * path existed and was invisible, which for a user is the same as not existing.
 *
 * ⭐⭐ THE TWO PLATFORMS NEED TWO DIFFERENT THINGS. Android Chrome fires `beforeinstallprompt`,
 * which we capture and replay from a real button. **iOS fires nothing, ever** - Add to Home
 * Screen is only reachable through the Share sheet, so there the card DESCRIBES the steps
 * instead of offering a button that could not work.
 *
 * ⛔ It renders nothing inside the Capacitor shell, or once installed. See `installPrompt.ts`.
 */

/** The captured Chrome event. Not in lib.dom - it is still non-standard. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const INSTALL_DISMISSED_KEY = 'apex.installPrompt.dismissed';

/**
 * 🚨🚨 THE EVENT IS ALREADY GONE BY THE TIME REACT MOUNTS - measured on a Pixel 3a, with every
 * test in this component's suite green. Chrome fires `beforeinstallprompt` during page load, so
 * the inline script in `index.html` catches it and parks it here. This reads what that caught.
 */
const parkedEvent = (): BeforeInstallPromptEvent | null =>
  safely(
    () => (window as { __apexInstallEvent?: BeforeInstallPromptEvent }).__apexInstallEvent ?? null,
    null
  );

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(parkedEvent);
  const [dismissed, setDismissed] = useState(() =>
    safely(() => localStorage.getItem(INSTALL_DISMISSED_KEY) !== null, false)
  );

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Still listened for directly: Chrome can fire it again later in the session, and this
      // path is what runs in a browser that fires it after mount.
      // 🚨 preventDefault matters - without it Chrome shows its own mini-infobar and our card
      // becomes a second, competing offer. (index.html calls it first for the early firing.)
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    // The inline script announces an event it caught before React existed.
    const onParked = () => setDeferred(parkedEvent());
    const onInstalled = () => {
      setDeferred(null);
      setDismissed(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('apex:installavailable', onParked);
    window.addEventListener('appinstalled', onInstalled);
    // Re-check once after mount, in case the announcement landed between the initial state
    // being read and this effect running.
    onParked();
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('apex:installavailable', onParked);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    safely(() => localStorage.setItem(INSTALL_DISMISSED_KEY, new Date().toISOString()), undefined);
    setDismissed(true);
  }, []);

  /** The parked copy has to go too, or a remount resurrects a spent event. */
  const clearParked = () =>
    safely(() => {
      (window as { __apexInstallEvent?: unknown }).__apexInstallEvent = null;
    }, undefined);

  const install = useCallback(async () => {
    if (!deferred) return;
    await deferred.prompt();
    // The event is single-use whatever the answer, so the card retires either way; if he
    // declined, Chrome will fire a fresh one on a later visit.
    setDeferred(null);
    clearParked();
    const choice = await safely(
      () => deferred.userChoice,
      Promise.resolve({ outcome: 'dismissed' as const })
    );
    if (choice.outcome === 'accepted') dismiss();
  }, [deferred, dismiss]);

  const offer = resolveInstallOffer(readInstallEnvironment(Boolean(deferred)));
  if (dismissed || offer === 'none') return null;

  return (
    /*
     * Pinned to the bottom, where a phone's thumb is, and clear of the home indicator:
     * `viewport-fit=cover` draws the page edge to edge, so without the inset this card sits
     * UNDER the indicator on every notch iPhone. `pb-[calc(...)]` keeps the padding when the
     * inset is 0 (Android, desktop) instead of collapsing the card.
     */
    <div
      role="dialog"
      aria-label="Install the Apex app"
      className="fixed inset-x-0 bottom-0 z-[2300] px-3 pb-[calc(env(safe-area-inset-bottom)+12px)] md:hidden"
    >
      <div className="mx-auto flex max-w-[420px] items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
        <img src="/icons/icon-192.png" alt="" className="h-9 w-9 shrink-0 rounded-md" />

        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold leading-tight text-gray-900">
            Install Apex on this phone
          </p>

          {offer === 'event' ? (
            <p className="mt-0.5 text-[12px] leading-snug text-gray-600">
              Its own icon and full screen, with no browser bar.
            </p>
          ) : (
            /*
             * ⚠️ iOS FIRES NO EVENT, so this is the whole feature on his phone: the two
             * controls named the way they are labelled on screen, in the order he taps them.
             */
            <p className="mt-0.5 text-[12px] leading-snug text-gray-600">
              Tap <span className="font-semibold text-gray-900">Share</span> in Safari, then{' '}
              <span className="font-semibold text-gray-900">Add to Home Screen</span>.
            </p>
          )}

          {offer === 'event' && (
            <button
              type="button"
              onClick={install}
              className="mt-2 h-[30px] rounded-md bg-primary-500 px-3 text-[11.5px] font-semibold text-white hover:bg-primary-600 active:bg-primary-700"
            >
              Install app
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="-mr-1 -mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-900"
        >
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7">
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
